import { ErrorHandler, Injectable } from "@angular/core";
import { LoggingService } from "../logging.service";
import { EventsService } from "../../services/tracking/events.service";

@Injectable()
export class ErrorLoggerService implements ErrorHandler {
    private static consolePatched = false;

    constructor(private logger: LoggingService, private eventsService: EventsService) {
        this.initUndefinedErrorLogging();
        this.initConsoleErrorLogging();
    }

    /**
     * angular's error handler
     * @param error 
     */
    handleError(error: any): void {
        const message = error.message || error.toString();
        const stack = error.stack || 'No Stack';

        // Layer 2: The Angular Net
        this.logger.logError({
            context: 'UNCAUGHT_ERROR',
            message,
            stack
        });
        this.captureToPosthog('UNCAUGHT_ERROR', message, stack);

        // IMPORTANT: Keep the default console behavior for Devs
        console.error(error); 
    }
   
    /**
     * used for errors that are not catched (uncaught errors)
     */
    private initUndefinedErrorLogging() {
        window.addEventListener('error', (event) => {
            this.logger.logError({
                context: 'UNDEFINED_ERROR',
                message: event.message,
                stack: event.error?.stack || 'No Stack'
            });
            this.captureToPosthog('UNDEFINED_ERROR', event.message, event.error?.stack || 'No Stack');
        });

        // Also catch "Uncaught Promise Rejections" (very common in Angular HttpClient)
        window.addEventListener('unhandledrejection', (event) => {
            const reason = event.reason;
            const message: string = reason?.message ?? String(reason ?? '');
            const stack: string = reason?.stack || 'No Stack';

            // ion-icon (Ionic 6) leaks an unhandled `Failed to fetch` rejection
            // when its SVG fetch is aborted during route changes. Harmless but
            // pollutes the console. Suppress only this exact pattern so real
            // fetch failures (e.g. HttpClient) still surface.
            if (message === 'Failed to fetch' && /loadIcon|waitUntilVisible/.test(stack)) {
                event.preventDefault();
                return;
            }

            this.logger.logError({
                context: 'UNHANDLED_PROMISE_REJECTION',
                message,
                stack
            });
            this.captureToPosthog('UNHANDLED_PROMISE_REJECTION', message, stack);
        });
    }

    private static _logging = false;

    private initConsoleErrorLogging() {
        if (ErrorLoggerService.consolePatched) {
            return;
        }

        ErrorLoggerService.consolePatched = true;
        const originalConsoleError = console.error.bind(console);
        console.error = (...args: any[]) => {
            originalConsoleError(...args);
            if (ErrorLoggerService._logging) return;
            ErrorLoggerService._logging = true;
            try {
                const message = args.map((arg) => typeof arg === 'string' ? arg : JSON.stringify(arg)).join(' | ');
                this.captureToPosthog('CONSOLE_ERROR', message || 'console.error called', 'No Stack');
            } catch {}
            ErrorLoggerService._logging = false;
        };
    }

    private captureToPosthog(context: string, message: string, stack: string) {
        if (ErrorLoggerService._logging) return;
        try {
            this.eventsService.logCustomEvent('app_error', {
                error_context: context,
                error_message: message,
                stack: stack || 'No Stack'
            });
        } catch {}
    }
}
